import { Component, ElementRef, EventEmitter, Inject, OnInit, Output, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { lastValueFrom } from 'rxjs';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { environment } from 'src/environments/environment';
import { PartnerContractService } from 'src/app/views/registration/partners/services/partner-contract.service';
import { ContractGenerationService } from '../../../services/contract-generation.service';

export interface ContractPreviewDialogData {
    contractData?: any;
    generationData?: any;
    previewData?: { partnerId: string; contractType: string; systemId?: string };
    customPdfBlob?: Blob;
    contractTypeName?: string;
    useCustomTemplate?: boolean;
    isPartnerPreview?: boolean;
}

@Component({
    selector: 'app-contract-preview-dialog',
    templateUrl: './contract-preview-dialog.component.html',
    styleUrls: ['./contract-preview-dialog.component.sass']
})
export class ContractPreviewDialogComponent implements OnInit {
    @ViewChild('pdfViewer') pdfViewer: ElementRef | null = null;
    @Output() confirmGeneration = new EventEmitter<any>();

    pdfUrl: SafeResourceUrl | null = null;
    loading: boolean = true;
    isGenerating: boolean = false;
    error: boolean = false;
    errorMessage: string = '';
    objectUrl: string | null = null;
    isDebugMode: boolean = !environment.production;
    showHelp: boolean = false;
    private pdfBlob: Blob | null = null;

    constructor(
        public dialogRef: MatDialogRef<ContractPreviewDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ContractPreviewDialogData,
        private contractGenerationService: ContractGenerationService,
        private partnerContractService: PartnerContractService,
        private sanitizer: DomSanitizer,
        private toastService: ToastService,
    ) { }

    ngOnInit(): void {
        this.loadPreview();
    }

    async loadPreview(): Promise<void> {
        this.loading = true;
        this.isGenerating = false;
        this.error = false;
        this.errorMessage = '';
        this.showHelp = false;

        if (this.objectUrl) {
            URL.revokeObjectURL(this.objectUrl);
            this.objectUrl = null;
            this.pdfUrl = null;
        }

        if (this.data.customPdfBlob) {
            this.processPdfBlob(this.data.customPdfBlob);
            return;
        }

        try {
            let previewBlob: Blob | undefined;

            if (this.data.isPartnerPreview && this.data.previewData) {
                previewBlob = await lastValueFrom(
                    this.partnerContractService.previewContract(this.data.previewData)
                );
            } else if (this.data.contractData) {
                previewBlob = await lastValueFrom(
                    this.contractGenerationService.previewContract(this.data.contractData)
                );
            } else {
                throw new Error('Dados insuficientes para gerar pré-visualização.');
            }

            if (!previewBlob) {
                throw new Error('API não retornou resultado.');
            }

            this.processPdfBlob(previewBlob);
        } catch (err: any) {
            this.handlePreviewError(err);
        }
    }

    private processPdfBlob(pdfBlob: Blob): void {
        if (!pdfBlob || pdfBlob.size === 0) {
            this.handlePreviewError(new Error('O documento retornado está vazio ou corrompido.'));
            return;
        }

        try {
            this.pdfBlob = new Blob([pdfBlob], { type: 'application/pdf' });
            this.objectUrl = URL.createObjectURL(this.pdfBlob);
            this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);

            setTimeout(() => {
                this.loading = false;
            }, 500);
        } catch (e: any) {
            this.handlePreviewError(e);
        }
    }

    private handlePreviewError(error: Error): void {
        this.toastService.showError('Erro ao carregar a prévia do contrato.');
        this.error = true;
        this.errorMessage = error.message || 'Não foi possível processar o documento.';
        this.loading = false;
        this.isGenerating = false;
    }

    downloadPdf(): void {
        if (!this.objectUrl || !this.pdfBlob) {
            this.toastService.showError('Não foi possível baixar o documento.');
            return;
        }

        try {
            const link = document.createElement('a');
            link.href = this.objectUrl;
            link.download = `contrato-preview-${new Date().getTime()}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (e) {
            this.toastService.showError('Erro ao baixar o documento.');
        }
    }

    openPdfInNewTab(): void {
        if (!this.objectUrl) {
            this.toastService.showError('Não foi possível abrir o documento.');
            return;
        }

        try {
            window.open(this.objectUrl as string, '_blank');
        } catch (e) {
            this.toastService.showError('Erro ao abrir o documento em nova aba.');
        }
    }

    confirm(): void {
        if (this.isGenerating) return;

        this.isGenerating = true;
        this.loading = true;

        const generationData = this.data.generationData || this.data.contractData;
        this.confirmGeneration.emit(generationData);
    }

    setGenerating(isGenerating: boolean): void {
        this.isGenerating = isGenerating;
        this.loading = isGenerating;
    }

    close(): void {
        if (this.isGenerating) return;

        if (this.objectUrl) {
            URL.revokeObjectURL(this.objectUrl);
        }

        this.dialogRef.close();
    }
} 